"use client";

import { useCallback, useEffect, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import {
  useCanEditContent,
  useCanManageWorkspace,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateOffer } from "@/app/actions/offer/update/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProjectOfferData } from "@/app/types/client-avatar/UserClientAvatar";
import { PageParams } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import OfferDetail from "../OfferDetail/OfferDetail";
import styles from "./OfferCard.module.css";

type Props = {
  params: PageParams;
  offer: ProjectOfferData;
  projectId: number;
  onSaved?: () => void;
  onDeleted?: () => void;
};

export default function OfferCard(props: Props) {
  const T = useT();
  const { params, offer: offerProp, projectId, onSaved, onDeleted } = props;
  const { lang } = params;

  const canEdit = useCanEditContent();
  const canManage = useCanManageWorkspace();

  const [offer, setOffer] = useState(offerProp);

  useEffect(() => {
    setOffer(offerProp);
  }, [offerProp]);

  const save = useCallback(
    async (patch: Partial<ProjectOfferData>) => {
      const next = { ...offer, ...patch };
      try {
        const updated = await updateOffer(offer.id, {
          name: next.name,
          type: next.type || undefined,
          problem: next.problem || undefined,
          promise: next.promise || undefined,
          price: next.price || undefined,
          proof: next.proof,
          keyAdvantages: next.keyAdvantages,
        });
        setOffer(updated);
        onSaved?.();
      } catch {
        setOffer(offer);
      }
    },
    [offer, onSaved],
  );

  const handleDelete = () => {
    ModalStore.getState().openModal(
      Components.OfferDeletion,
      T(TKey.OFFER_DELETE_TITLE) as string,
      { params, projectId, offer, onDeleted },
      ModalType.SMALL,
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <EditableText
          value={offer.name}
          onSave={(val) => save({ name: val })}
          required={true}
          disabled={!canEdit}
          fitContent={true}
          alignCenter={true}
          maxLength={Limits.MAX_OFFER_NAME}
          className={styles.nameEdit}
          textClassName={styles.name}
          inputClassName={styles.nameInput}
        />
        {canManage && (
          <div className={styles.menu}>
            <ActionMenu
              items={[
                {
                  icon: <IconTrash />,
                  label: T(TKey.OFFER_DELETE_CONFIRM) as string,
                  onClick: handleDelete,
                  danger: true,
                },
              ]}
            />
          </div>
        )}
      </div>

      <OfferDetail offer={offer} lang={lang} canEdit={canEdit} onSave={save} />
    </div>
  );
}
